import React, { FC, useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { useMediaQuery } from 'react-responsive'
import { Button } from '@/atoms/Button'
import { CaretLeftIcon } from '@/atoms/Icons/CaretLeftIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { paths } from '@/constants'
import { Modal } from '@/molecules/Modal'
import { SocialSharing } from '@/organisms/SocialSharing'
import { createBasicShareLink, useNativeMobileShare } from '@/services/Sharing'
import { useTranslate } from '@/utils/translate/translate-client'

const cardClassName =
  'relative flex h-[140px] w-full flex-col overflow-hidden rounded-2xl border border-transparent p-4 text-left hover:border-white md:h-[180px]'

interface Props {
  theatricalSlug: string
  projectName: string
  discountCodes: string[]
  isOpen: boolean
  onClose: () => void
}

export const GuildTicketsModal: FC<Props> = ({ theatricalSlug, projectName, discountCodes, isOpen, onClose }) => {
  const { t } = useTranslate('tickets')
  const [modalState, setModalState] = useState<'options' | 'gift'>('options')
  const [shareLink, setShareLink] = useState(
    `/tickets/${theatricalSlug}?flow=guild-gift&discounts=${discountCodes.join(',')}`,
  )

  const { share } = useNativeMobileShare()
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' })

  useEffect(() => {
    createBasicShareLink({
      baseUrl: `${paths.base}/tickets/${theatricalSlug}`,
      params: { flow: 'guild-gift', discounts: discountCodes.join(',') },
    }).then((url: string) => setShareLink(url))
  }, [discountCodes, theatricalSlug])

  const handleShareClick = useCallback(async () => {
    if (isMobile) {
      await share({
        title: t('movieTicketGift', 'Movie Ticket Gift'),
        text: t('movieTicketsGiftDescriptions', 'Movie tickets to go watch {{projectName}} in theaters!', {
          projectName,
        }),
        url: shareLink,
        onShareFail: () => {
          setModalState('gift')
        },
      })
    } else {
      setModalState('gift')
    }
  }, [isMobile, projectName, share, shareLink, t])

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={isOpen}
      onClose={onClose}
      panelClasses="!bg-core-gray-200 min-h-[392px] inline-flex md:min-h-[532px] rounded-3xl !max-w-[480px] !w-full !p-4 !pb-6 !pt-10 md:!pt-12 md:!p-10"
      transition="appear"
      className="!px-4"
      childWrapperClassName="!m-0 flex-1"
    >
      <div className="absolute right-2 top-2 p-3" onClick={onClose}>
        <CloseIcon color="black" size={24} />
      </div>

      {modalState === 'options' && (
        <div>
          <Text color="black" weight="semibold" className="photon-title-xs md:photon-title-md">
            {t('howWouldYouLikeToUseYourGuildTickets', 'How would you like to use your Guild Tickets?')}
          </Text>

          <div className="mt-4 flex flex-col gap-2 md:mt-6">
            <Link
              href={`/tickets/${theatricalSlug}?discounts=${discountCodes.join(',')}`}
              className={classNames(cardClassName, 'group')}
            >
              <Image src="v1743374663/movie-theater-seating-00_hk12fd.webp" fill alt="" className="object-cover" />
              <span
                style={{
                  background: 'linear-gradient(96.31deg, rgba(113, 22, 1, 0.85) 25%, rgba(113, 22, 1, 0) 100%)',
                }}
                className="absolute inset-0 "
              />
              <span className="relative z-10 flex flex-col gap-1">
                <Text weight="bold" color="white" className="photon-title-lg md:photon-heading-xs">
                  {t('pickYourSeats', 'Pick Your Seats')}
                </Text>
                <Text color="white" className="photon-title-xxs md:photon-title-xs">
                  {t('atATheaterNearYou', 'at a Theater Near You')}
                </Text>
              </span>
              <div className="absolute inset-0 bg-black/30 opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100" />
            </Link>

            <button onClick={handleShareClick} className={classNames(cardClassName, 'group')}>
              <Image
                src="v1743374663/qr-code-hands-ticket-ai-gen-image-01b_ijg9dc.webp"
                fill
                alt=""
                className="object-cover"
              />
              <span
                style={{
                  background: 'linear-gradient(96.31deg, rgba(51, 15, 77, 0.85) 25%, rgba(51, 15, 77, 0) 100%)',
                }}
                className="absolute inset-0"
              />
              <span className="relative z-10 flex flex-col gap-1">
                <Text weight="bold" color="white" className="photon-title-lg md:photon-heading-xs">
                  {t('giftYourTickets', 'Gift Your Tickets')}
                </Text>
                <Text color="white" className="photon-title-xxs md:photon-title-xs">
                  {t('toYourFriendsAndFamily', 'to your Friends & Family')}
                </Text>
              </span>
              <div className="absolute inset-0 bg-black/30 opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100" />
            </button>
          </div>
        </div>
      )}

      {modalState === 'gift' && (
        <div className="flex min-h-full flex-1 flex-col ">
          <SocialSharing url={shareLink} vertical={true} color="black" iconWrapperClassName="fill-black" />

          <Button
            onClick={() => setModalState('options')}
            variant="white"
            className="ml-4 mr-auto mt-auto w-fit border-black/10 !bg-black/10 hover:border-black/10"
            size="md"
          >
            <CaretLeftIcon size={24} color="black" />
            Back
          </Button>
        </div>
      )}
    </Modal>
  )
}
